"use client";

import React from "react";
import { Loader, Send, Sparkles, CircleCheck, CircleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const BriefingForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel>Your Role</FormLabel>
              <FormControl>
                <Input placeholder="CTO, Head of Innovation, etc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="industry" render={({
      field
    }) => <FormItem>
            <FormLabel>Industry</FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none" {...field} value={field.value || ""}>
                  <option value="" disabled className="bg-card text-muted-foreground">
                    Select your industry
                  </option>
                  {["Financial Services", "Healthcare", "Manufacturing", "Retail", "Education", "Technology", "Other"].map(option => <option key={option} value={option} className="bg-card text-foreground">
                      {option}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="brief_description" render={({
      field
    }) => <FormItem>
            <FormLabel>Brief Description of Your Innovation Goals</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" placeholder="e.g., Looking to implement AI-driven predictive analytics for supply chain optimization." {...field} />
            </FormControl>
            <FormDescription>
              Provide any specific topics or challenges you’d like the briefing to cover (optional).
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2 text-destructive text-sm">
          <CircleAlert className="h-4 w-4" />
          <p>{submitError}</p>
        </div>}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(6,182,212,0.3)]">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Processing Request...
            </> : <>
              Request Briefing
              <Send className="ml-2 h-4 w-4" />
            </>}
        </Button>
      </div>
    </Form>;
};
export default function InnovationBriefing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="innovation-briefing" className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-blue-900/20 rounded-full blur-[120px]" style={{
        animation: "pulse 8s infinite reverse"
      }} />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="text-center mb-10 space-y-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge variant="outline" className="border-primary/50 text-primary bg-primary/10 px-3 py-1 text-sm backdrop-blur-sm">
                <Sparkles className="w-3 h-3 mr-1" />
                New
              </Badge>
              <span className="text-sm font-medium text-muted-foreground tracking-wider uppercase">
                Intelligence Report
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white drop-shadow-sm">
              Get the <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">Intelligence</span>
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Subscribe to our exclusive innovation briefing. Receive curated insights on AI trends, cloud security, and digital transformation strategies tailored for the Kenyan market.
            </p>
          </div>

          {}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/30 to-blue-600/30 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
            <div className="relative bg-card/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-10 shadow-2xl">
              <FormProvider formId="innovation_briefing_insights" sectionName="innovation-briefing" defaultValues={{
              full_name: "",
              email: "",
              company: "",
              role: "",
              industry: "",
              brief_description: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              company: {
                required: true
              }
            }} submitText="Request Briefing" successMessage="Thank you! Your briefing request has been received. We will be in touch shortly.">
                <BriefingForm />
              </FormProvider>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground">
              By subscribing, you agree to our privacy policy. We respect your inbox and only send high-value intelligence.
            </p>
          </div>
        </div>
      </div>
    </section>;
}